/**
 * Partnership allocation by profit percentage (spec 2026-09-26, 7.2 "1065":
 * "Allocation to K-1s by profit percentage").
 *
 * Every Schedule K amount is split across the partners in whole dollars, and
 * the partners' shares must add up to the Schedule K total exactly (each K-1
 * line summed over the partners equals the Schedule K line). Rounding each
 * share on its own can miss by a dollar, so this uses the largest-remainder
 * method: every partner gets the whole-dollar part of their exact share, and
 * the dollars left over go one each to the partners with the largest
 * fractional parts (ties: the partner listed first).
 *
 * The API's `mmllc` return model and the customer wizard's partner step and
 * Summary both call this, so the customer sees the same numbers the K-1s print.
 */

/** Percentages are whole or two-decimal numbers; compare in hundredths of a percent. */
function basisPoints(percent: number): number {
  return Math.round(percent * 100);
}

/** True when the percentages add up to exactly 100 (to two decimals) and none is negative. */
export function percentagesTotal100(percentages: readonly number[]): boolean {
  if (percentages.length === 0) return false;
  if (percentages.some((p) => !Number.isFinite(p) || p < 0)) return false;
  return (
    percentages.reduce((total, p) => total + basisPoints(p), 0) === 100 * 100
  );
}

/**
 * Split a whole-dollar `total` by `percentages` (which must total 100) so the
 * returned shares sum to `total` exactly. A negative total (a loss) is split
 * the same way.
 */
export function allocateByPercentage(
  total: number,
  percentages: readonly number[],
): number[] {
  if (!Number.isInteger(total)) {
    throw new Error(`allocateByPercentage: ${total} is not whole dollars`);
  }
  if (!percentagesTotal100(percentages)) {
    throw new Error(
      `allocateByPercentage: percentages must total 100 (got ${percentages.join(" + ")})`,
    );
  }
  const sign = total < 0 ? -1 : 1;
  const magnitude = Math.abs(total);
  // Exact share in 1/10000 dollars: magnitude * basis points (integer arithmetic).
  const exact = percentages.map((p) => magnitude * basisPoints(p));
  const floors = exact.map((value) => Math.floor(value / 10_000));
  let left = magnitude - floors.reduce((a, b) => a + b, 0);
  const order = exact
    .map((value, index) => ({ index, remainder: value % 10_000 }))
    .sort((a, b) => b.remainder - a.remainder || a.index - b.index);
  for (const { index } of order) {
    if (left <= 0) break;
    floors[index]! += 1;
    left -= 1;
  }
  return floors.map((share) => (share === 0 ? 0 : sign * share));
}

/**
 * One Schedule K line split across the partners (Form 1065 item J: profit,
 * loss and capital shares "as determined under the partnership agreement",
 * i1065 p. 33). Income follows the profit shares; a loss follows the loss
 * shares, or the profit shares when the loss shares do not add up to 100.
 * The API's `mmllc` return model, the wizard's partner step and Summary and
 * its K-1 review all call this, so the three show the same K-1 numbers.
 */
export function allocateScheduleKLine(
  total: number,
  shares: { profit: readonly number[]; loss: readonly number[] },
): number[] {
  const byLoss = total < 0 && percentagesTotal100(shares.loss);
  return allocateByPercentage(total, byLoss ? shares.loss : shares.profit);
}

/**
 * The stored key of the wizard's "are these two members family?" answer
 * (Form 1065 Schedule B question 2b, i1065 2025 p. 26: family attribution
 * under section 267(c)). The same pair gives the same key in either order.
 */
export function memberFamilyKey(memberId: string, otherId: string): string {
  const [first, second] = [memberId, otherId].sort();
  return `family_${first}_${second}`;
}
